import * as WidgetApi from '@wix/ambassador-innovation-widget-v1-message/types';

import { DuplexerConnection, DuplexerChannel } from '@/services/duplexer';

export const enum DuplexerMessageType {
  NewMessageEvent = 'new-message-event',
  NewConversationEvent = 'new-conversation-event',
  ConversationChannelEvent = 'message',
}

/**
 * Typed realtime channel published by wix-assistant-widget (`realtime_access.proto`).
 * Subscribed with `resourceId` = conversation id; every event is `message` with a
 * `ConversationChannelMessage` payload.
 */
export const CONVERSATION_CHANNEL = 'conversation';

export type NewMessageEvent = WidgetApi.Message;
export type MessageTypingEvent = {
  expiryMs: number;
  answerTo: string;
  conversationId: string;
};
export type DuplexerMessage = NewMessageEvent | MessageTypingEvent;
/**
 * Payload of the typed `conversation` channel (`ConversationMessage` in
 * `realtime_access.proto`): exactly one of `message` or `typing`.
 */
export type ConversationChannelMessage =
  | { message: NewMessageEvent; typing?: undefined }
  | { typing: MessageTypingEvent; message?: undefined };

export const isConversationNewMessage = (
  payload: ConversationChannelMessage,
): payload is { message: NewMessageEvent } => payload.message != null;

export const isConversationTyping = (
  payload: ConversationChannelMessage,
): payload is { typing: MessageTypingEvent } => payload.typing != null;

export type DuplexerMergeConversationsMessage = {
  oldConversationIds: string[];
  targetConversationId: string;
};

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

// Runtime checks of payloads received from Duplexer, shared by both channels.
const isNewMessagePayload = (value: unknown): value is NewMessageEvent =>
  isObject(value) && typeof value.id === 'string' && value.id.length > 0;

const isTypingPayload = (value: unknown): value is MessageTypingEvent =>
  isObject(value) && typeof value.expiryMs === 'number';

/**
 * Runtime check of a `conversation` channel payload: exactly one of a valid
 * `message` or a valid `typing`.
 */
export const isConversationChannelMessage = (
  payload: unknown,
): payload is ConversationChannelMessage =>
  isObject(payload) &&
  (isNewMessagePayload(payload.message)
    ? payload.typing == null
    : isTypingPayload(payload.typing) && payload.message == null);

// Legacy channel: the payload is a raw message or typing event, told apart by
// shape. Remove together with the legacy channel.
const fromLegacyPayload = (
  payload: unknown,
): ConversationChannelMessage | undefined => {
  if (isNewMessagePayload(payload)) {
    return { message: payload };
  }
  if (isTypingPayload(payload)) {
    return { typing: payload };
  }
  return undefined;
};

export class ConversationChannel {
  private readonly channel: DuplexerChannel;

  constructor(
    public readonly id: string,
    connection: DuplexerConnection,
    private readonly typed = false,
  ) {
    this.channel = typed
      ? connection.subscribe(CONVERSATION_CHANNEL, { resourceId: id })
      : connection.subscribe(id);
  }

  onConversationMessage(
    handler: (payload: ConversationChannelMessage) => unknown,
  ) {
    if (this.typed) {
      this.channel.on(
        DuplexerMessageType.ConversationChannelEvent,
        (payload: unknown) => {
          return isConversationChannelMessage(payload)
            ? handler(payload)
            : undefined;
        },
      );
      return;
    }

    this.channel.on(DuplexerMessageType.NewMessageEvent, (payload: unknown) => {
      const message = fromLegacyPayload(payload);
      return message ? handler(message) : undefined;
    });
  }

  onNewConversationEvent(
    handler: (conversation: DuplexerMergeConversationsMessage) => void,
  ) {
    this.channel.on(DuplexerMessageType.NewConversationEvent, handler);
  }

  close() {
    this.removeAllListeners();
    this.unsubscribe();
  }

  unsubscribe() {
    this.channel.unsubscribe();
  }

  removeAllListeners() {
    this.channel.removeAllListeners();
  }
}

export const createConversationChannel = (
  conversationId: string,
  connection: DuplexerConnection,
  typed = false,
): ConversationChannel => {
  return new ConversationChannel(conversationId, connection, typed);
};
